"use strict";

import store from 'store';
import Config from './Config';

const BYPASS_QUERY_PARAM = 'maintenance_bypass';
const BYPASS_STORAGE_KEY = 'maintenance_bypass_token';
const BYPASS_REVOKE_VALUE = 'off';

/**
 * An unset token must never grant bypass, otherwise `?maintenance_bypass=`
 * would match an empty config value and unlock the site for everyone.
 *
 * @return {?string}
 * @private
 */
function getConfiguredBypassToken() {
    const token = Config.MAINTENANCE_BYPASS_TOKEN;
    return typeof token === 'string' && token.length ? token : null;
}

/**
 * @param {string} pathname
 * @return {boolean}
 */
export function isOnMaintenancePath(pathname) {
    const path = Config.ROOT + 'maintenance';
    return pathname === path || pathname.indexOf(path) === 0;
}

/**
 * The token itself is persisted rather than a boolean, so changing
 * MAINTENANCE_BYPASS_TOKEN revokes every tester's bypass on the next deploy.
 *
 * @return {boolean}
 */
export function isMaintenanceBypassGranted() {
    const configured = getConfiguredBypassToken();
    return !!configured && store.get(BYPASS_STORAGE_KEY) === configured;
}

/**
 * Read `?maintenance_bypass=<token>` and persist it for this browser profile.
 * `off` revokes, so a tester can return to the customer experience without
 * clearing localStorage, which would also destroy migration state.
 *
 * @param {string} [search] location search string
 * @return {boolean} whether bypass is granted after this call
 */
export function consumeMaintenanceBypassToken(search = window.location.search) {
    const value = new URLSearchParams(search).get(BYPASS_QUERY_PARAM);
    if (value === null) {
        return isMaintenanceBypassGranted();
    }

    if (value === BYPASS_REVOKE_VALUE) {
        store.remove(BYPASS_STORAGE_KEY);
        return false;
    }

    const configured = getConfiguredBypassToken();
    if (!configured || value !== configured) {
        return isMaintenanceBypassGranted();
    }

    store.set(BYPASS_STORAGE_KEY, configured);
    return true;
}

/**
 * Remove the bypass token from the address bar, keeping any other query params.
 *
 * AppContainer registers `router.listen` on mount and react-router v3 invokes
 * that listener immediately with the current location, so leaving the token in
 * place would send it to Google Analytics via trackPageView, browser history
 * and referrer headers.
 */
export function stripMaintenanceBypassParam() {
    if (!window.history || typeof window.history.replaceState !== 'function') {
        return;
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.has(BYPASS_QUERY_PARAM)) {
        return;
    }

    params.delete(BYPASS_QUERY_PARAM);
    const query = params.toString();
    const url = window.location.pathname
        + (query ? `?${query}` : '')
        + window.location.hash;

    window.history.replaceState({}, document.title, url);
}

/**
 * The single maintenance predicate. The router guard and AppContainer must both
 * call this one function; if they evaluate bypass independently they disagree
 * and loop between /maintenance and the requested route.
 *
 * Maintenance is unconditional so the page can be verified on production: there
 * is no environment flag, and the bypass token is the only way through. Removing
 * maintenance means reverting this whole change, not flipping a setting.
 *
 * @return {boolean}
 */
export function isMaintenanceModeActive() {
    return !isMaintenanceBypassGranted();
}
